import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { businessConfig, trackPhoneClick } from '../config/business';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { contact, assets } = businessConfig;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Service Areas', path: '/areas' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'FAQ', path: '/faq' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-gray-900 shadow-lg border-b border-gray-800' 
            : 'bg-gray-900'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-18 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src={assets.logo}
                alt="Thrifty Move - Melbourne Removalists"
                className="h-12 w-auto md:h-16"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`transition-colors duration-200 font-medium text-sm ${
                    isActive(link.path) 
                      ? 'text-yellow-400' 
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <a 
                href={contact.phoneLink}
                onClick={() => trackPhoneClick('Header - Call')}
                className="flex items-center gap-2"
                data-phone-number="true"
              >
                <div className="text-right hidden xl:block">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Call Free</div>
                  <div className="text-base font-bold text-white" data-phone-number="true">{contact.phone}</div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-gray-900 font-semibold px-4 py-2"
                >
                  <Phone className="w-4 h-4 xl:mr-2" />
                  <span className="hidden xl:inline">Call</span>
                </Button>
              </a>
              <Link to="/quote">
                <Button className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-5 py-2">
                  Get a Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-2">
              <Link to="/quote">
                <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-sm px-3 py-2">
                  Quote
                </Button>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white p-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-gray-800 border-t border-gray-700 max-h-[calc(100vh-72px)] overflow-y-auto">
            <nav className="container mx-auto px-4 py-4 flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`py-3 border-b border-gray-700 transition-colors duration-200 font-medium ${
                    isActive(link.path)
                      ? 'text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile CTA Section */}
              <div className="flex flex-col gap-3 pt-4 mt-2">
                <Link to="/quote">
                  <Button className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-3">
                    Get a Free Quote
                  </Button>
                </Link>
                <a 
                  href={contact.phoneLink}
                  onClick={() => trackPhoneClick('Mobile Menu - Call')}
                  data-phone-number="true"
                >
                  <Button variant="outline" className="w-full border-white text-white hover:bg-white hover:text-gray-900 font-semibold py-3">
                    <Phone className="w-4 h-4 mr-2" />
                    <span data-phone-number="true">{contact.phone}</span>
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
